// client/src/pages/Pricing.js
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../components/ui/Button.js';
import Modal from '../components/ui/Modal.js';
import { useSettings } from '../hooks/useSettings.js';
import { useAuth } from '../hooks/useAuth.js';
import api from '../services/api.js';

export default function Pricing() {
  const { userTypes } = useSettings();
  const { user, loadUser } = useAuth();
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [trialTiers, setTrialTiers] = useState([]);
  const [activatingTrial, setActivatingTrial] = useState(null);

  // Plan change modal state
  const [showModal, setShowModal] = useState(false);
  const [modalTier, setModalTier] = useState(null);
  const [modalInterval, setModalInterval] = useState('monthly');

  const allTiers = userTypes || [];
  const freeTier = allTiers.find((t) => !t.ispaid);
  const paidTiers = allTiers.filter((t) => t.ispaid && t.isenabled);
  const currentTier = allTiers.find((t) => t.usertypeid === user?.usertype);
  const compareTiers = allTiers.filter((t) => t.isenabled);

  // Trial detection
  const isOnTrial = user?.trial_expires_at && new Date(user.trial_expires_at) > new Date() && !user.stripecustomerid;
  const trialDaysLeft = isOnTrial ? Math.ceil((new Date(user.trial_expires_at) - new Date()) / (1000 * 60 * 60 * 24)) : 0;
  const hasUsedTrial = user?.has_used_trial || false;
  const canStartTrial = user && !hasUsedTrial && !user.tier?.ispaid && !isOnTrial;

  // Total visible cards for grid calculation
  const totalCards = (freeTier ? 1 : 0) + paidTiers.length;

  // Fetch public trial info (works for logged-out users too)
  useEffect(() => {
    const fetchTrialInfo = async () => {
      try {
        const data = await api.get('/public/trial-info', false);
        setTrialTiers(data.trialTiers || []);
      } catch {
        setTrialTiers([]);
      }
    };
    fetchTrialInfo();
  }, []);

  // Check if a specific tier has a trial
  const getTierTrialInfo = (tierId) => {
    return trialTiers.find((t) => t.usertypeid === tierId);
  };

  const formatPrice = (amount) => {
    const num = parseFloat(amount);
    if (!num) return 'Free';
    return `$${num.toFixed(2)}`;
  };

  const handleSubscribe = (tier, interval) => {
    if (!user) {
      sessionStorage.setItem('pendingPlan', JSON.stringify({
        tier: tier.usertypeid,
        interval,
        tierName: tier.typename,
      }));
      navigate('/register');
      return;
    }

    // Trial users go straight to checkout — no modal, no plan change flow
    if (isOnTrial) {
      navigate(`/checkout?tier=${tier.usertypeid}&interval=${interval}`);
      return;
    }

    // Logged in, currently on a paid subscription — show confirmation modal
    if (user.tier?.ispaid && user.stripecustomerid) {
      setModalTier(tier);
      setModalInterval(interval);
      setShowModal(true);
      return;
    }

    // Logged in, on free plan — go straight to checkout
    navigate(`/checkout?tier=${tier.usertypeid}&interval=${interval}`);
  };

  const handleStartTrial = async (tierId) => {
    if (!user) {
      sessionStorage.setItem('pendingTrial', String(tierId));
      navigate('/register');
      return;
    }

    setActivatingTrial(tierId);
    try {
      await api.post('/auth/activate-trial', { tierId });
      await loadUser();
      window.location.reload();
    } catch (err) {
      alert(err.data?.error || 'Unable to start trial');
    }
    setActivatingTrial(null);
  };

  // Modal helpers
  const isUpgrade = modalTier && currentTier && modalTier.usertypeid > currentTier.usertypeid;
  const isSamePlan = modalTier && currentTier && modalTier.usertypeid === currentTier.usertypeid;

  const handleModalConfirm = () => {
    setShowModal(false);
    navigate(`/account/change-plan?tier=${modalTier.usertypeid}&interval=${modalInterval}`);
  };

  // Comparison table
  const comparisonFeatures = [
    { label: 'Contacts', key: 'maxcontacts', format: (v) => v === -1 ? 'Unlimited' : v >= 100000 ? 'Unlimited' : v.toLocaleString() },
    { label: 'Deliveries', key: 'maxdeliveriesmonth', format: (v, t) => t.plandeliverytypeid === 3 ? 'Unlimited' : t.plandeliverytypeid === 2 ? `${t.maxdeliverytotalfreeaccount || 0} total` : `${v}/mo` },
    { label: 'Send via email or link', key: 'cansend', format: (v) => v ? '\u2713' : '\u2014' },
    { label: 'Schedule sending', key: 'canschedule', format: (v) => v ? '\u2713' : '\u2014' },
    { label: 'Birthday auto-send', key: 'autobirthday', format: (v) => v ? '\u2713' : '\u2014' },
    { label: 'Photo uploads', key: 'maxuserimages', format: (v) => v > 0 ? '\u2713' : '\u2014' },
    { label: 'Analytics dashboard', key: 'canviewanalytics', format: (v) => v ? '\u2713' : '\u2014' },
    { label: 'Trackable links', key: 'canuselinks', format: (v) => v ? '\u2713' : '\u2014' },
    { label: 'CSV upload', key: 'canuploadcsv', format: (v) => v ? '\u2713' : '\u2014' },
    { label: 'Card life', key: 'cardexpirydays', format: (v) => v === 0 ? 'Never expires' : v >= 365 ? `${Math.round(v / 30)} months` : `${v} days` },
  ];

  const faqs = [
    { q: 'Can I try before I subscribe?', a: 'Absolutely. Free accounts can preview every card template with full animations and music. Upgrade when you\'re ready to send.' },
    { q: 'Can I change plans later?', a: 'Yes. Upgrade or downgrade anytime from your Account page. Changes take effect on your next billing cycle.' },
    { q: 'What is your refund policy?', a: 'For annual plans, if you cancel early you\'ll receive a prorated refund for the unused months remaining — and your account ends immediately upon cancellation. For monthly plans, there are no refunds; you keep full access until the end of your current billing period and will not be charged again after that. You\'re always welcome to re-subscribe at any time.' },
    { q: 'What happens when a card expires?', a: 'Cards do not expire. But, in the event a user deletes their account, all connected cards will be deleted.' },
    { q: 'Is there a contract?', a: 'No contracts. All plans are month-to-month or yearly. Cancel anytime from your Account page.' },
    { q: 'What payment methods do you accept?', a: 'We accept all major credit and debit cards through Stripe. Payment info is handled securely and never touches our servers.' },
  ];

  // Dynamic grid classes based on total cards
  const gridCols = totalCards <= 2
    ? 'grid-cols-1 sm:grid-cols-2 max-w-2xl'
    : totalCards === 3
    ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 max-w-5xl'
    : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 max-w-6xl';

  return (
    <div className="min-h-screen bg-cream dark:bg-bg-dark transition-colors duration-300">
      <div className="max-w-6xl mx-auto px-4 py-8 sm:py-12">

        {/* Header */}
        <div className="text-center mb-8 sm:mb-12">
          <h1 className="font-display text-3xl sm:text-4xl text-gold-dark dark:text-gold mb-3">Choose Your Plan</h1>
          <p className="text-royal-600 dark:text-royal-100 text-base max-w-2xl mx-auto">
            Send beautifully animated cards with music to the people you love. Every plan includes all card templates and music.
          </p>
        </div>

        {/* Trial Banner — active trial user */}
        {isOnTrial && (
          <div className="max-w-3xl mx-auto mb-8">
            <div className="p-4 bg-gold/10 border border-gold/30 rounded-xl text-center">
              <p className="text-gold font-medium text-base mb-1">
                ✨ You're on a free trial — {trialDaysLeft} day{trialDaysLeft !== 1 ? 's' : ''} left
              </p>
              <p className="text-royal-700 dark:text-white text-sm">
                Subscribe now to keep your features after the trial ends.
              </p>
            </div>
          </div>
        )}

        {/* Trial Banner — not logged in, trials available */}
        {!user && trialTiers.length > 0 && (
          <div className="max-w-3xl mx-auto mb-8">
            <div className="p-4 bg-gold/10 border border-gold/30 rounded-xl text-center">
              <p className="text-gold font-medium text-base mb-1">
                ✨ Free trial available — no credit card required
              </p>
              <p className="text-royal-700 dark:text-white text-sm">
                Create an account and try premium features free for up to {Math.max(...trialTiers.map(t => t.trial_duration_days))} days.
              </p>
            </div>
          </div>
        )}

        {/* Trial used banner — logged in user who has used their one free trial */}
        {user && hasUsedTrial && !isOnTrial && !user.stripecustomerid && trialTiers.length > 0 && (
          <div className="max-w-3xl mx-auto mb-8">
            <div className="p-4 bg-royal-900 dark:bg-royal-800/40 border border-royal-700 dark:border-royal-600 rounded-xl text-center">
              <p className="text-royal-200 dark:text-royal-200 text-sm">
                Your one-time free trial has ended. Subscribe to any plan to continue enjoying premium features.
              </p>
            </div>
          </div>
        )}

        {/* Billing toggle */}
        {paidTiers.length > 0 && (
          <div className="flex justify-center mb-8 sm:mb-10">
            <div className="inline-flex rounded-full border border-royal-700 dark:border-royal-600 p-1 bg-royal-900 dark:bg-royal-800">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`px-5 sm:px-6 py-2 rounded-full text-sm font-medium transition-colors ${
                  billingCycle === 'monthly'
                    ? 'bg-gold text-royal-900'
                    : 'text-white hover:text-gold'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle('yearly')}
                className={`px-5 sm:px-6 py-2 rounded-full text-sm font-medium transition-colors ${
                  billingCycle === 'yearly'
                    ? 'bg-gold text-royal-900'
                    : 'text-white hover:text-gold'
                }`}
              >
                Yearly
                <span className="ml-1 text-sm opacity-75">save more</span>
              </button>
            </div>
          </div>
        )}

        {/* Pricing Cards — adaptive centered grid */}
        <div className={`grid ${gridCols} gap-5 sm:gap-6 mb-10 mx-auto`}>

          {/* Free Tier */}
          {freeTier && (
            <div className="rounded-2xl p-5 sm:p-6 border border-royal-700 dark:border-royal-700/40 bg-royal-900 dark:bg-royal-800/20 flex flex-col">
              <h3 className="font-display text-xl text-white mb-1">{freeTier.typename}</h3>
              <p className="text-royal-100 text-sm mb-4">{freeTier.tagline}</p>

              <div className="mb-6">
                <span className="text-3xl font-bold text-white">Free</span>
               </div>

              <ul className="space-y-3 mb-6 flex-1">
                {(freeTier.features || []).map((f, i) => (
                  <li key={i} className="text-sm text-white flex items-start gap-2">
                    <span className="text-royal-100 mt-0.5">{'\u2713'}</span> {f}
                  </li>
                ))}
              </ul>

              {!user ? (
                <Button variant="outline" className="w-full" onClick={() => navigate('/register')}>
                  Get Started
                </Button>
              ) : user.usertype === freeTier.usertypeid && !isOnTrial ? (
                <p className="text-sm text-royal-100 text-center py-2">Current plan</p>
              ) : isOnTrial ? (
                <p className="text-sm text-royal-100 text-center py-2">You're on a free trial</p>
              ) : (
                <p className="text-sm text-royal-100 text-center py-2">Your current plan is better</p>
              )}
            </div>
          )}

          {/* Paid Tiers */}
          {paidTiers.map((tier) => {
            const monthly = parseFloat(tier.pricemonthly) || 0;
            const yearly = parseFloat(tier.priceyearly) || 0;
            const savePct = monthly > 0 ? Math.round((1 - yearly / (monthly * 12)) * 100) : 0;
            const isCurrentPaidPlan = user && user.usertype === tier.usertypeid && !isOnTrial && user.stripecustomerid;
            const tierTrial = getTierTrialInfo(tier.usertypeid);
            const hasTrial = !!tierTrial;
            const isTrialingThisTier = isOnTrial && user?.usertype === tier.usertypeid;

            return (
              <div
                key={tier.usertypeid}
                className={`rounded-2xl p-5 sm:p-6 border flex flex-col relative ${
                  tier.ispopular
                    ? 'border-gold bg-royal-900 dark:bg-royal-800/20 shadow-lg shadow-gold/10'
                    : 'border-royal-700 dark:border-royal-700/40 bg-royal-900 dark:bg-royal-800/20'
                }`}
              >
                {tier.ispopular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="text-sm font-bold text-royal-900 bg-gold-gradient px-4 py-1 rounded-full shadow-md">
                      Most Popular
                    </span>
                  </div>
                )}

                <h3 className="font-display text-xl text-gold mt-1 mb-1">{tier.typename}</h3>
                <p className="text-royal-100 text-sm mb-4">{tier.tagline}</p>

                <div className="mb-4">
                  {billingCycle === 'yearly' ? (
                    <>
                      <span className="text-3xl font-bold text-white">{formatPrice(yearly)}</span>
                      <span className="text-royal-100 text-sm">/year</span>
                      <div className="text-royal-100 text-sm mt-1">
                        {formatPrice(yearly / 12)}/month
                        {savePct > 0 && <span className="text-emerald-400 ml-1">· Save {savePct}%</span>}
                      </div>
                    </>
                  ) : (
                    <>
                      <span className="text-3xl font-bold text-white">{formatPrice(monthly)}</span>
                      <span className="text-royal-100 text-sm">/month</span>
                      {yearly > 0 && (
                        <div className="text-royal-100 text-sm mt-1">
                          or {formatPrice(yearly)}/year {savePct > 0 && <span className="text-emerald-400">(save {savePct}%)</span>}
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Trial badge — only for users who haven't used trial and aren't paying */}
                {hasTrial && !isCurrentPaidPlan && !isTrialingThisTier && !isOnTrial && !hasUsedTrial && !user?.stripecustomerid && (
                  <div className="mb-4 px-3 py-2 bg-gold/10 border border-gold/20 rounded-lg text-center">
                    <p className="text-gold text-sm font-medium">
                      ✨ {tierTrial.trial_duration_days}-day free trial available
                    </p>
                  </div>
                )}

                <ul className="space-y-3 mb-6 flex-1">
                  {(tier.features || []).map((f, i) => (
                    <li key={i} className="text-sm text-white flex items-start gap-2">
                      <span className="text-gold mt-0.5">{'\u2713'}</span> {f}
                    </li>
                  ))}
                </ul>

                {/* Action buttons */}
                {isCurrentPaidPlan ? (
                  <p className="text-sm text-gold text-center py-2 font-medium">Current plan</p>
                ) : isTrialingThisTier ? (
                  <div className="space-y-2">
                    <p className="text-sm text-gold text-center font-medium mb-1">
                      Trial active — {trialDaysLeft} day{trialDaysLeft !== 1 ? 's' : ''} left
                    </p>
                    {billingCycle === 'yearly' ? (
                      <Button
                        variant={tier.ispopular ? 'primary' : 'small'}
                        className="w-full py-3 text-base"
                        onClick={() => handleSubscribe(tier, 'yearly')}
                      >
                        Subscribe Yearly
                      </Button>
                    ) : (
                      <>
                        <Button
                          variant={tier.ispopular ? 'primary' : 'small'}
                          className="w-full py-2.5 text-sm"
                          onClick={() => handleSubscribe(tier, 'monthly')}
                        >
                          Subscribe Monthly
                        </Button>
                        {yearly > 0 && (
                          <Button
                            variant="outline"
                            className="w-full py-2.5 text-sm"
                            onClick={() => handleSubscribe(tier, 'yearly')}
                          >
                            Subscribe Yearly
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {/* Start Free Trial button */}
                    {hasTrial && !isOnTrial && (canStartTrial || !user) && (
                      <button
                        onClick={() => handleStartTrial(tier.usertypeid)}
                        disabled={activatingTrial === tier.usertypeid}
                        className="w-full py-2.5 rounded-lg font-medium text-base transition-colors bg-gold/15 text-gold border border-gold/30 hover:bg-gold/25 disabled:opacity-60"
                      >
                        {activatingTrial === tier.usertypeid ? 'Starting...' : `Start ${tierTrial.trial_duration_days}-Day Free Trial`}
                      </button>
                    )}

                    {billingCycle === 'yearly' ? (
                      <Button
                        variant={tier.ispopular ? 'primary' : 'small'}
                        className="w-full py-3 text-base"
                        onClick={() => handleSubscribe(tier, 'yearly')}
                      >
                        Subscribe Yearly
                      </Button>
                    ) : (
                      <>
                        <Button
                          variant={tier.ispopular ? 'primary' : 'small'}
                          className="w-full py-2.5 text-sm"
                          onClick={() => handleSubscribe(tier, billingCycle)}
                        >
                          Subscribe Monthly
                        </Button>
                        {yearly > 0 && (
                          <Button
                            variant="outline"
                            className="w-full py-2.5 text-sm"
                            onClick={() => handleSubscribe(tier, 'yearly')}
                          >
                            Subscribe Yearly
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* ─── Gift code redemption section ────────────────
            Placed between the plan cards and the comparison table.
            Visible to everyone — /gift/accept handles auth state itself
            (logged-out users get a login prompt on the redeem page,
            logged-in users go straight through). One consistent entry
            point regardless of who's looking. */}
        <div className="max-w-md mx-auto mb-12 sm:mb-16">
          <div className="flex items-center gap-3 mb-5">
            <div className="flex-1 h-px bg-royal-300 dark:bg-royal-700/60" />
            <span className="text-royal-500 dark:text-royal-200 text-sm whitespace-nowrap">or, if you have one</span>
            <div className="flex-1 h-px bg-royal-300 dark:bg-royal-700/60" />
          </div>

          <div className="rounded-xl border border-gold/40 bg-gold/5 dark:bg-gold/10 p-5 sm:p-6 text-center">
            <div className="text-3xl mb-2">🎁</div>
            <h3 className="font-display text-lg text-gold-dark dark:text-gold mb-1">
              Have a Gift Code?
            </h3>
            <p className="text-royal-700 dark:text-royal-100 text-sm mb-4">
              Redeem it to unlock your subscription.
            </p>
            <button
              onClick={() => navigate('/gift/accept')}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-gold text-royal-900 font-semibold rounded-lg text-base hover:bg-gold-light transition-colors"
            >
              Redeem a Gift
              <span aria-hidden="true">→</span>
            </button>
          </div>
        </div>

        {/* Feature Comparison Table */}
        {compareTiers.length > 0 && (
          <div className="mb-16 sm:mb-20">
            <h2 className="font-display text-2xl text-gold-dark dark:text-gold text-center mb-8">Compare Plans</h2>
            <div className="overflow-x-auto max-w-5xl mx-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-royal-300 dark:border-royal-600">
                    <th className="text-left text-royal-800 dark:text-royal-100 text-sm py-3 px-3 sm:px-4 font-normal" style={{width: '30%'}}>Feature</th>
                    {compareTiers.map((t) => (
                      <th key={t.usertypeid} className="text-center text-gold-dark dark:text-gold text-sm py-3 px-3 sm:px-4 font-display">
                        {t.typename}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {comparisonFeatures.map((feat, i) => (
                    <tr key={feat.key} className={`border-b border-royal-200 dark:border-royal-700/50 ${i % 2 === 0 ? 'bg-royal-100/50 dark:bg-royal-800/20' : ''}`}>
                      <td className="text-royal-900 dark:text-white text-sm py-3 px-3 sm:px-4">{feat.label}</td>
                      {compareTiers.map((t) => (
                        <td key={t.usertypeid} className="text-center text-sm py-3 px-3 sm:px-4">
                          <span className={
                            feat.format(t[feat.key], t) === '\u2713'
                              ? 'text-emerald-600 dark:text-emerald-400'
                              : feat.format(t[feat.key], t) === '\u2014'
                              ? 'text-royal-400 dark:text-royal-400'
                              : 'text-royal-900 dark:text-white'
                          }>
                            {feat.format(t[feat.key], t)}
                          </span>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* FAQ */}
        <div className="max-w-3xl mx-auto mb-12 sm:mb-16">
          <h2 className="font-display text-2xl text-gold-dark dark:text-gold text-center mb-8">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="border border-royal-700 dark:border-royal-700/40 rounded-xl p-4 sm:p-5 bg-royal-900 dark:bg-royal-800/20">
                <h3 className="text-white font-medium mb-2">{faq.q}</h3>
                <p className="text-royal-100 text-sm leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center pb-8">
          <p className="text-royal-600 dark:text-royal-100 text-sm">
            Questions? Reach out anytime. We're happy to help you find the right plan.
          </p>
        </div>

        {/* Plan change confirmation modal — only for PAID subscribers (not trial users) */}
        <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={
          isSamePlan ? 'Current Plan' : isUpgrade ? 'Upgrade Plan' : 'Switch Plan'
        }>
          {isSamePlan ? (
            <>
              <p className="text-royal-200 mb-6">
                You're already on the <strong className="text-gold">{currentTier?.typename}</strong> plan. No changes needed!
              </p>
              <Button variant="outline" onClick={() => setShowModal(false)} className="w-full">
                Got it
              </Button>
            </>
          ) : isUpgrade ? (
            <>
              <p className="text-royal-200 mb-4">
                You're currently on <strong className="text-white">{currentTier?.typename}</strong>.
                Upgrade to <strong className="text-gold">{modalTier?.typename}</strong>?
              </p>
              {modalTier?.features && (
                <div className="bg-royal-800/60 border border-royal-600 rounded-lg p-4 mb-4">
                  <p className="text-royal-200 text-sm uppercase tracking-wider mb-2">You'll gain access to</p>
                  <ul className="space-y-1">
                    {(modalTier.features || [])
                      .filter((f) => !(currentTier?.features || []).includes(f))
                      .slice(0, 4)
                      .map((f, i) => (
                        <li key={i} className="text-sm text-white flex items-start gap-2">
                          <span className="text-green-400 shrink-0">+</span>
                          <span>{f}</span>
                        </li>
                      ))}
                  </ul>
                </div>
              )}
              <p className="text-royal-200 text-sm mb-5">
                Any unused time on your current plan will be credited toward the new price.
              </p>
              <div className="flex gap-3">
                <Button onClick={handleModalConfirm} className="flex-1">
                  Upgrade to {modalTier?.typename}
                </Button>
                <Button variant="ghost" onClick={() => setShowModal(false)} className="flex-1">
                  Not now
                </Button>
              </div>
            </>
          ) : (
            <>
              <p className="text-royal-200 mb-4">
                You're currently on <strong className="text-white">{currentTier?.typename}</strong>.
                Switch to <strong className="text-gold">{modalTier?.typename}</strong>?
              </p>
              {currentTier?.features && (
                <div className="bg-royal-800/60 border border-royal-600 rounded-lg p-4 mb-4">
                  <p className="text-royal-200 text-sm uppercase tracking-wider mb-2">You'll lose access to</p>
                  <ul className="space-y-1">
                    {(currentTier.features || [])
                      .filter((f) => !(modalTier?.features || []).includes(f))
                      .slice(0, 4)
                      .map((f, i) => (
                        <li key={i} className="text-sm text-white flex items-start gap-2">
                          <span className="text-red-400 shrink-0">&minus;</span>
                          <span>{f}</span>
                        </li>
                      ))}
                  </ul>
                </div>
              )}
              <p className="text-royal-200 text-sm mb-5">
                Any unused time on your current plan will be credited toward the new price.
              </p>
              <div className="flex gap-3">
                <Button onClick={handleModalConfirm} className="flex-1">
                  Continue
                </Button>
                <Button variant="ghost" onClick={() => setShowModal(false)} className="flex-1">
                  Keep my plan
                </Button>
              </div>
            </>
          )}
        </Modal>
      </div>
    </div>
  );
}